import * as THREE from 'three'
import {
  GSplatShaderEffect,
  type GSplatShaderEffectOptions,
} from '@/plugins/gsplat/effects/GSplatShaderEffect'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import type { GSplatWorkBufferModifier } from '@/shared/types/gsplat'
import {
  createGSplatTreesGpuResources,
  type GSplatTreesGpuResources,
} from './GSplatTreesGpuResources'
import {
  buildGSplatTreesPlan,
  GSPLAT_TREES_BIND_STREAM,
  type GSplatTreeSphere,
  type GSplatTreesPlan,
} from './GSplatTreesPlan'
import {
  GSPLAT_TREES_COPY_GLSL,
  GSPLAT_TREES_COPY_WGSL,
  GSPLAT_TREES_RENDER_GLSL,
  GSPLAT_TREES_RENDER_WGSL,
} from './GSplatTreesShaders'

export interface GSplatTreesOptions extends GSplatShaderEffectOptions {
  readonly strength?: number
  readonly speed?: number
  /** Horizontal wind direction in degrees, using the engine XY plane. */
  readonly direction?: number
  readonly gustiness?: number
  readonly flutter?: number
  readonly waveTravel?: number
  readonly editSelected?: number
  readonly spheres?: readonly GSplatTreeSphere[]
}

export interface GSplatTreesCopyPayload {
  readonly sphereData: Float32Array
  readonly gridData: Float32Array
  readonly listData: Float32Array
  readonly gridMin: readonly [number, number]
  readonly gridInvCell: readonly [number, number]
  readonly gridDims: readonly [number, number]
}

/**
 * Backend-neutral Trees runtime.
 *
 * The class owns the deterministic sphere/grid plan and final render effect.
 * The caller is responsible for publishing `treeBind` to the System format
 * transaction and uploading `copyPayload` to adapter-owned textures. This
 * separation keeps GPU resource ownership out of the effect and makes failed
 * preflight/rebuilds side-effect free.
 */
export class GSplatTrees extends GSplatShaderEffect {
  readonly bindStreamName = GSPLAT_TREES_BIND_STREAM

  private spheresValue: readonly GSplatTreeSphere[] = Object.freeze([])
  private planValue: GSplatTreesPlan = buildGSplatTreesPlan([])
  private strengthValue: number
  private speedValue: number
  private directionValue: number
  private gustinessValue: number
  private flutterValue: number
  private waveTravelValue: number
  private editSelectedValue: number
  private readonly changeListeners = new Set<() => void>()
  private revisionValue = 0

  constructor(options: GSplatTreesOptions = {}) {
    super({
      ...options,
      id: options.id ?? 'trees',
      scope: options.scope ?? 'shared-material',
      uniforms: {
        uTime: { value: 0 },
        uWindDir: { value: new THREE.Vector2(1, 0) },
        uWindStrength: { value: options.strength ?? 0.25 },
        uWindSpeed: { value: options.speed ?? 1 },
        uGustiness: { value: options.gustiness ?? 0.4 },
        uFlutter: { value: options.flutter ?? 0.3 },
        uWaveTravel: { value: options.waveTravel ?? 2.5 },
        uEditSelected: { value: options.editSelected ?? -1 },
        ...(options.uniforms ?? {}),
      },
    })
    this.strengthValue = finiteNonNegative(options.strength ?? 0.25, 'strength')
    this.speedValue = finiteNonNegative(options.speed ?? 1, 'speed')
    this.directionValue = finiteNumber(options.direction ?? 45, 'direction')
    this.gustinessValue = finiteNonNegative(options.gustiness ?? 0.4, 'gustiness')
    this.flutterValue = finiteNonNegative(options.flutter ?? 0.3, 'flutter')
    this.waveTravelValue = finiteNonNegative(options.waveTravel ?? 2.5, 'waveTravel')
    this.editSelectedValue = finiteNumber(options.editSelected ?? -1, 'editSelected')
    this.setSpheres(options.spheres ?? [])
  }

  get strength(): number {
    return this.strengthValue
  }

  set strength(value: number) {
    const next = finiteNonNegative(value, 'strength')
    if (this.strengthValue === next) return
    this.strengthValue = next
    this.syncUniforms()
    this.notifyChanged()
  }

  get speed(): number {
    return this.speedValue
  }

  set speed(value: number) {
    const next = finiteNonNegative(value, 'speed')
    if (this.speedValue === next) return
    this.speedValue = next
    this.syncUniforms()
    this.notifyChanged()
  }

  get direction(): number {
    return this.directionValue
  }

  set direction(value: number) {
    const next = finiteNumber(value, 'direction')
    if (this.directionValue === next) return
    this.directionValue = next
    this.syncUniforms()
    this.notifyChanged()
  }

  get gustiness(): number {
    return this.gustinessValue
  }

  set gustiness(value: number) {
    const next = finiteNonNegative(value, 'gustiness')
    if (this.gustinessValue === next) return
    this.gustinessValue = next
    this.syncUniforms()
    this.notifyChanged()
  }

  get flutter(): number {
    return this.flutterValue
  }

  set flutter(value: number) {
    const next = finiteNonNegative(value, 'flutter')
    if (this.flutterValue === next) return
    this.flutterValue = next
    this.syncUniforms()
    this.notifyChanged()
  }

  get waveTravel(): number {
    return this.waveTravelValue
  }

  set waveTravel(value: number) {
    const next = finiteNonNegative(value, 'waveTravel')
    if (this.waveTravelValue === next) return
    this.waveTravelValue = next
    this.syncUniforms()
    this.notifyChanged()
  }

  get editSelected(): number {
    return this.editSelectedValue
  }

  set editSelected(value: number) {
    const next = finiteNumber(value, 'editSelected')
    if (this.editSelectedValue === next) return
    this.editSelectedValue = next
    this.syncUniforms()
    this.notifyChanged()
  }

  get spheres(): readonly GSplatTreeSphere[] {
    return this.spheresValue
  }

  get revision(): number {
    return this.revisionValue
  }

  subscribe(listener: () => void): () => void {
    this.changeListeners.add(listener)
    return () => this.changeListeners.delete(listener)
  }

  get plan(): GSplatTreesPlan {
    return this.planValue
  }

  get copyPayload(): GSplatTreesCopyPayload {
    return {
      sphereData: this.planValue.sphereData,
      gridData: this.planValue.gridData,
      listData: this.planValue.listData,
      gridMin: this.planValue.gridMin,
      gridInvCell: this.planValue.gridInvCell,
      gridDims: this.planValue.gridDims,
    }
  }

  /**
   * Create the adapter-owned textures consumed by the WorkBuffer copy modifier.
   * The returned lease is independent from this effect and must be disposed by
   * the caller after it has been detached from its placement parameters.
   */
  createGpuResources(
    adapter: GSplatGraphicsAdapter,
    label = `gsplat-trees:${this.id}`
  ): GSplatTreesGpuResources {
    return createGSplatTreesGpuResources(adapter, this.planValue, label)
  }

  get workBufferModifier(): GSplatWorkBufferModifier {
    return Object.freeze({
      glsl: GSPLAT_TREES_COPY_GLSL,
      wgsl: GSPLAT_TREES_COPY_WGSL,
    })
  }

  get formatStream(): GSplatStreamDescriptor {
    return Object.freeze({
      name: GSPLAT_TREES_BIND_STREAM,
      format: 'rgba8unorm',
      storage: 'instance',
    })
  }

  /**
   * Let the System publish the permanent stream through its own transaction.
   * The callback is deliberately injected to keep this effect independent of
   * a concrete System/Manager implementation.
   */
  ensureFormat(publish: (streams: readonly GSplatStreamDescriptor[]) => boolean): boolean {
    return publish([this.formatStream])
  }

  /** True only when a non-empty tree set can change over time. */
  get needsContinuousActivity(): boolean {
    return (
      this.enabled &&
      this.spheresValue.length > 0 &&
      this.strengthValue !== 0 &&
      (this.speedValue !== 0 || this.gustinessValue !== 0 || this.flutterValue !== 0)
    )
  }

  setSpheres(spheres: readonly GSplatTreeSphere[]): void {
    this.updateSpheres(spheres, false)
  }

  refresh(): void {
    this.updateSpheres(this.spheresValue, true)
  }

  private updateSpheres(spheres: readonly GSplatTreeSphere[], force: boolean): void {
    const next = buildGSplatTreesPlan(spheres)
    if (!force && sameTreePlan(this.planValue, next)) return
    this.spheresValue = Object.freeze(
      next.spheres.map((sphere) =>
        Object.freeze({ center: sphere.center.clone(), radius: sphere.radius })
      )
    )
    this.planValue = next
    this.notifyChanged()
  }

  override getShaderGLSL(): string {
    return GSPLAT_TREES_RENDER_GLSL
  }

  override getShaderWGSL(): string {
    return GSPLAT_TREES_RENDER_WGSL
  }

  protected override updateEffect(effectTime: number): void {
    const angle = (this.directionValue * Math.PI) / 180
    this.setUniform('uTime', effectTime)
    this.setUniform('uWindDir', new THREE.Vector2(Math.cos(angle), Math.sin(angle)))
    this.setUniform('uWindStrength', this.strengthValue)
    this.setUniform('uWindSpeed', this.speedValue)
    this.setUniform('uGustiness', this.gustinessValue)
    this.setUniform('uFlutter', this.flutterValue)
    this.setUniform('uWaveTravel', this.waveTravelValue)
    this.setUniform('uEditSelected', this.editSelectedValue)
  }

  private notifyChanged(): void {
    this.revisionValue += 1
    for (const listener of this.changeListeners) listener()
  }
}

function finiteNumber(value: number, name: string): number {
  if (!Number.isFinite(value)) throw new Error(`GSplatTrees ${name} 必须是有限数`)
  return value
}

function finiteNonNegative(value: number, name: string): number {
  const normalized = finiteNumber(value, name)
  if (normalized < 0) throw new Error(`GSplatTrees ${name} 不能为负数`)
  return normalized
}

function sameTreePlan(left: GSplatTreesPlan, right: GSplatTreesPlan): boolean {
  if (
    left.spheres.length !== right.spheres.length ||
    left.gridData.length !== right.gridData.length ||
    left.listData.length !== right.listData.length
  ) {
    return false
  }
  for (let index = 0; index < left.spheres.length; index += 1) {
    const previous = left.spheres[index]
    const next = right.spheres[index]
    if (previous.radius !== next.radius || !previous.center.equals(next.center)) {
      return false
    }
  }
  return (
    left.gridMin[0] === right.gridMin[0] &&
    left.gridMin[1] === right.gridMin[1] &&
    left.gridInvCell[0] === right.gridInvCell[0] &&
    left.gridInvCell[1] === right.gridInvCell[1] &&
    left.gridDims[0] === right.gridDims[0] &&
    left.gridDims[1] === right.gridDims[1] &&
    equalFloatArray(left.gridData, right.gridData) &&
    equalFloatArray(left.listData, right.listData)
  )
}

function equalFloatArray(left: Float32Array, right: Float32Array): boolean {
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false
  }
  return true
}
